import { useLayoutEffect, useRef, type RefObject } from 'react'

const EASE = 'cubic-bezier(0.2, 0, 0, 1)'

/**
 * Rows that change place glide to their new spot instead of jumping (John,
 * Nov 5 2026: strategy rows "glitch move fast up/down"). FLIP by hand, the
 * way Apple's and Coinbase's lists move: each row's layout position
 * (offsetTop, which ignores a glide still running) is remembered after every
 * render; on the next, a row found elsewhere is put back where it was with a
 * transform and let go, all before the browser paints, so the eye sees one
 * continuous move. Nothing moves under prefers-reduced-motion. Rows are the
 * elements inside `ref` carrying `data-flip-key`.
 */
export function useFlipRows(ref: RefObject<HTMLElement | null>, key: string, durationMs = 240) {
  const last = useRef<Map<string, number> | null>(null)
  useLayoutEffect(() => {
    const root = ref.current
    if (!root) return
    const rows = Array.from(root.querySelectorAll<HTMLElement>('[data-flip-key]'))
    const now = new Map(rows.map((el) => [el.dataset.flipKey as string, el.offsetTop]))
    const before = last.current
    last.current = now
    if (!before || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const moved: { el: HTMLElement; delta: number }[] = []
    for (const el of rows) {
      const id = el.dataset.flipKey as string
      const from = before.get(id)
      const to = now.get(id)
      if (from === undefined || to === undefined) continue
      const delta = from - to
      if (Math.abs(delta) >= 1) moved.push({ el, delta })
    }
    if (moved.length === 0) return
    // invert: every moved row back where it was, in one layout pass
    for (const { el, delta } of moved) {
      el.style.transition = 'none'
      el.style.transform = `translateY(${delta}px)`
    }
    void root.offsetHeight
    // play: let go; the inline styles clear once each row has arrived
    for (const { el } of moved) {
      el.style.transition = `transform ${durationMs}ms ${EASE}`
      el.style.transform = ''
      const done = (event: TransitionEvent) => {
        if (event.target !== el) return
        el.style.transition = ''
        el.removeEventListener('transitionend', done)
      }
      el.addEventListener('transitionend', done)
    }
  }, [ref, key, durationMs])
}
